import { useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "./store";
import { initTelegram } from "./lib/telegram";
import { api, setBearerToken } from "./lib/api";
import { WebAppAuthResponse } from "./types/api";
import { Loading } from "./pages/Loading";
import { ElementPicker } from "./pages/ElementPicker";
import { ErrorView } from "./pages/ErrorView";
import { BottomNav, Tab } from "./components/BottomNav";
import { CastleScreen } from "./pages/CastleScreen";
import { ArmyScreen } from "./pages/ArmyScreen";
import { MeScreen } from "./pages/MeScreen";
import { MarchesScreen } from "./pages/MarchesScreen";
import { QuestsScreen } from "./pages/QuestsScreen";
import { InventoryScreen } from "./pages/InventoryScreen";
import { AllianceScreen } from "./pages/AllianceScreen";
import { ChatScreen } from "./pages/ChatScreen";
import { GiPerson } from "react-icons/gi";
import { useMe } from "./api/army";
import { useCastle } from "./api/castle";
import { BuildingType } from "./types/castle";
import { useElementTheme } from "./lib/elementTheme";
import { ElementalBackdrop } from "./components/ElementalBackdrop";
import { useQuests } from "./api/quests";
import { useNewbieChest } from "./api/newbieChest";
import { TutorialOverlay } from "./components/TutorialOverlay";
import { buildTutorialSteps } from "./components/tutorialSteps";

function ThemedShell({ children }: { children: React.ReactNode }) {
  const me = useMe();
  useElementTheme(me.data?.element);
  return (
    <>
      <ElementalBackdrop element={me.data?.element} />
      {children}
    </>
  );
}

interface TutorialGateProps {
  onSwitchTab: (t: Tab) => void;
}

function TutorialGate({ onSwitchTab }: TutorialGateProps) {
  const me = useMe();
  const displayName = useSession((s) => s.displayName);
  const quests = useQuests(me.data != null);
  const [dismissed, setDismissed] = useState(false);
  if (
    dismissed ||
    !me.data ||
    !quests.data ||
    quests.data.tutorialCompleted
  ) {
    return null;
  }
  const steps = buildTutorialSteps(displayName, me.data.element);
  return (
    <TutorialOverlay
      steps={steps}
      onSwitchTab={(t) => onSwitchTab(t as Tab)}
      onComplete={() => setDismissed(true)}
    />
  );
}

export default function App() {
  const status = useSession((s) => s.status);
  const errorMessage = useSession((s) => s.errorMessage);

  const setAuthenticating = useSession((s) => s.setAuthenticating);
  const setAuthenticated = useSession((s) => s.setAuthenticated);
  const setNotRegistered = useSession((s) => s.setNotRegistered);
  const setError = useSession((s) => s.setError);

  const [tab, setTab] = useState<Tab>("castle");
  const initDataRef = useRef<string>("");

  useEffect(() => {
    if (status !== "idle") return;
    setAuthenticating();

    const { initData, ready } = initTelegram();
    if (!ready || !initData) {
      setError("Это приложение нужно открывать внутри Telegram.");
      return;
    }
    initDataRef.current = initData;

    api
      .post<WebAppAuthResponse>("/webapp/auth", { initData })
      .then((res) => {
        const body = res.data;
        if (!body.isRegistered) {
          setNotRegistered(body.telegramId);
          return;
        }
        setBearerToken(body.token);
        setAuthenticated({
          playerId: body.playerId,
          worldId: body.worldId,
          telegramId: body.telegramId,
          displayName: body.firstName,
        });
      })
      .catch((err: unknown) => {
        const msg =
          err && typeof err === "object" && "message" in err
            ? String((err as { message: unknown }).message)
            : "Не удалось подключиться к серверу.";
        setError(msg);
      });
  }, [status, setAuthenticating, setAuthenticated, setNotRegistered, setError]);

  if (status === "idle" || status === "authenticating") {
    return <Loading message="Вход в королевство..." />;
  }
  if (status === "not_registered") {
    return (
      <ElementPicker
        initData={initDataRef.current}
        onRegistered={setAuthenticated}
      />
    );
  }
  if (status === "error") {
    return <ErrorView message={errorMessage ?? "Неизвестная ошибка."} />;
  }

  return (
    <ThemedShell>
      <NavShell tab={tab} setTab={setTab} />
    </ThemedShell>
  );
}

// Inner shell that reads castle level (must run inside ThemedShell so useMe
// has fired and JWT is set). Owns the bottom nav + screen routing + a guard
// that bumps the active tab back to Castle if the player ends up on a
// not-yet-revealed tab (e.g. browser back, deep link).
// Tab order — used to compute slide direction so the new screen comes from
// the natural side relative to where the player tapped.
const TabOrder: Tab[] = ["castle", "army", "inventory", "alliance", "marches", "quests"];

function NavShell({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  const castle = useCastle();
  const quests = useQuests();
  const newbieChest = useNewbieChest();
  const [profileOpen, setProfileOpen] = useState(false);
  // Tabs unlock with the corresponding building actually being built (not
  // merely revealed). Mere reveal at Castle Lvl 3 wouldn't help — the player
  // still has to spend resources to build it before Армия / Марши become
  // useful screens.
  const buildings = castle.data?.state.buildings ?? [];
  const hasBarracks =
    (buildings.find((b) => b.type === BuildingType.Barracks)?.level ?? 0) >= 1;
  const hasWarHall =
    (buildings.find((b) => b.type === BuildingType.WarHall)?.level ?? 0) >= 1;
  const castleLevel = castle.data?.castleLevel ?? 1;

  // "Привет, забери награду" indicator on the Задания tab. Lights up when:
  //  - any onboarding or daily quest is completed and not yet claimed
  //  - a daily milestone chest (Bronze / Silver / Gold) is claimable
  //  - the newbie hourly chest is off cooldown and the player hasn't graduated
  const questsHasAttention = useMemo(() => {
    const q = quests.data;
    const c = newbieChest.data;
    const questClaimable = !!q && (
      q.onboarding.some((x) => x.claimable) ||
      q.daily.some((x) => x.claimable) ||
      q.dailyChests.some((x) => x.claimable)
    );
    const chestReady = !!c && !c.graduated && c.available;
    return questClaimable || chestReady;
  }, [quests.data, newbieChest.data]);

  // If the active tab isn't unlocked yet, snap back to Castle.
  useEffect(() => {
    if (tab === "army" && !hasBarracks) setTab("castle");
    if (tab === "marches" && !hasWarHall) setTab("castle");
    if (tab === "alliance" && castleLevel < 10) setTab("castle");
  }, [tab, hasBarracks, hasWarHall, castleLevel, setTab]);

  // Track the previous tab to determine slide direction. +1 = moving right
  // (new tab is right of old), -1 = moving left. Used as CSS var so the
  // shared keyframe knows which way to enter from.
  const prevTabRef = useRef<Tab>(tab);
  const tabDir = useMemo(() => {
    const prevIdx = TabOrder.indexOf(prevTabRef.current);
    const nextIdx = TabOrder.indexOf(tab);
    return nextIdx >= prevIdx ? 1 : -1;
  }, [tab]);
  useEffect(() => {
    prevTabRef.current = tab;
  }, [tab]);

  return (
    <>
      <div className="flex h-full flex-col">
        <div
          key={tab}
          className="animate-tab-slide-in flex-1 overflow-hidden"
          style={{ ["--tab-dir" as string]: String(tabDir) }}
        >
          {tab === "castle" && <CastleScreen />}
          {tab === "army" && <ArmyScreen />}
          {tab === "inventory" && <InventoryScreen />}
          {tab === "alliance" && <AllianceScreen />}
          {tab === "marches" && <MarchesScreen />}
          {tab === "chat" && <ChatScreen />}
          {tab === "quests" && <QuestsScreen />}
        </div>
        <BottomNav
          active={tab}
          onChange={setTab}
          hasBarracks={hasBarracks}
          hasWarHall={hasWarHall}
          castleLevel={castleLevel}
          questsHasAttention={questsHasAttention}
        />
        {/* Floating profile button — top-right of viewport. Opens MeScreen
            as a full-screen overlay. z-20 so that detail sheets / modals
            (HeroDetailSheet z-50, DropResultModal z-40 etc.) cover it
            instead of letting it float over per-screen content. */}
        <button
          type="button"
          onClick={() => setProfileOpen(true)}
          aria-label="Открыть профиль"
          className="fixed right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm transition-transform active:scale-95"
        >
          <GiPerson size={22} />
        </button>
      </div>
      {profileOpen && (
        <div className="fixed inset-0 z-40 bg-tg-bg">
          <div className="flex h-full flex-col">
            <div className="flex-1 overflow-hidden">
              <MeScreen />
            </div>
            <button
              type="button"
              onClick={() => setProfileOpen(false)}
              className="border-t border-tg-secondaryBg bg-tg-headerBg py-3 text-sm font-semibold uppercase tracking-wide text-tg-button"
            >
              Назад
            </button>
          </div>
        </div>
      )}
      <TutorialGate onSwitchTab={setTab} />
    </>
  );
}
